import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import {
  addPyramidLighting,
  createPyramidMark,
  disposePyramidMark,
  PYRAMID_CAMERA_DISTANCE,
  PYRAMID_CAMERA_FOV,
  PYRAMID_CAMERA_Y,
  PYRAMID_EDGE_ROTATION,
} from './pyramidMark';

/**
 * A small, self-contained spinning-pyramid mark for the partner headers.
 *
 * Reuses the exact geometry, finish and lighting of the main app's loading
 * splash (LoadingScreen), but instead of the one-shot
 * spin -> stop -> scroll-away sequence it loops forever:
 *   spin  ->  decelerate & snap to the nearest camera-facing edge  ->
 *   hold still briefly  ->  spin again  ->  …
 *
 * Honours prefers-reduced-motion (renders a single static frame) and pauses
 * while the tab is hidden.
 */

const ROTATION_SPEED = 0.02;
const SPIN_MS = 2400;
const HOLD_MS = 1000;
const MIN_SPEED = 0.0035;

type Phase = 'spin' | 'stopping' | 'hold';

export function PyramidSpinner({ size = 34, className }: { size?: number; className?: string }) {
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = canvasRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const aspectRatio = 1;
    const camera = new THREE.PerspectiveCamera(
      PYRAMID_CAMERA_FOV,
      aspectRatio,
      0.1,
      1000,
    );
    camera.position.set(0, PYRAMID_CAMERA_Y, PYRAMID_CAMERA_DISTANCE);
    camera.lookAt(0, PYRAMID_CAMERA_Y, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      precision: 'highp',
      powerPreference: 'high-performance',
      stencil: false,
    });
    renderer.setPixelRatio(window.devicePixelRatio > 1 ? 2 : window.devicePixelRatio);
    renderer.setSize(size, size);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    container.appendChild(renderer.domElement);

    const pyramidMark = createPyramidMark(renderer);
    const pyramid = pyramidMark.mesh;
    scene.add(pyramid);
    addPyramidLighting(scene);

    const halfTurn = Math.PI / 2;
    const nextEdge = (from: number): number => {
      const sector = Math.floor(from / halfTurn) + 1;
      return sector * halfTurn + PYRAMID_EDGE_ROTATION;
    };

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    let stopped = false;
    let phase: Phase = 'spin';
    let phaseStart = performance.now();
    let last = performance.now();
    let speed = ROTATION_SPEED;
    let target = 0;

    if (reduceMotion) {
      // Static: rest on a camera-facing edge, render one frame only.
      pyramid.rotation.y = PYRAMID_EDGE_ROTATION;
      renderer.render(scene, camera);
    }

    const animate = () => {
      if (stopped) return;
      raf = requestAnimationFrame(animate);
      if (document.hidden) return;

      const now = performance.now();
      const delta = Math.min((now - last) / 1000, 0.05);
      last = now;

      if (phase === 'spin') {
        speed = ROTATION_SPEED;
        if (now - phaseStart >= SPIN_MS) {
          phase = 'stopping';
          target = nextEdge(pyramid.rotation.y);
        }
      } else if (phase === 'stopping') {
        const cur = ((pyramid.rotation.y % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        const tgt = ((target % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        let dist = tgt - cur;
        if (dist < 0) dist += Math.PI * 2;
        // Ease out as we approach the edge, with a floor so it always arrives.
        speed = Math.max(MIN_SPEED, Math.min(ROTATION_SPEED, dist * 0.12));
        if (dist <= speed * delta * 60) {
          pyramid.rotation.y = target;
          speed = 0;
          phase = 'hold';
          phaseStart = now;
        }
      } else if (phase === 'hold') {
        speed = 0;
        if (now - phaseStart >= HOLD_MS) {
          phase = 'spin';
          phaseStart = now;
        }
      }

      pyramid.rotation.y += speed * delta * 60;
      renderer.render(scene, camera);
    };

    if (!reduceMotion) animate();

    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      renderer.dispose();
      renderer.forceContextLoss();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
      disposePyramidMark(pyramidMark);
      scene.clear();
    };
  }, [size]);

  return (
    <span
      className={`pyramid-spinner${className ? ` ${className}` : ''}`}
      ref={canvasRef}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
}
